'use client';

import React, { useEffect, useRef, useId } from 'react';
import { useModalLayer } from './use-modal-layer';
import { buttonVariants } from './button-variants';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  /** Title — appears as dialog heading. */
  title: string;
  /** Description — optional body text above actions. */
  description?: React.ReactNode;
  /** Confirm button label. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** Called when user clicks the confirm button. */
  onConfirm: () => void | Promise<void>;
  /** Destructive styling (red confirm button). Use for delete/close/revoke. */
  destructive?: boolean;
  /** Show loading spinner on confirm button. */
  loading?: boolean;
  /**
   * Disable the confirm button without the loading spinner (e.g. an
   * attestation checkbox that has not been ticked yet). Cancel stays enabled.
   */
  confirmDisabled?: boolean;
  /** Optional extra content rendered between description and actions. */
  children?: React.ReactNode;
}

/**
 * Styled alternative to `window.confirm()`. Use for destructive or
 * consequential actions (delete document, close incident, revoke access).
 *
 * Standard voice:
 *   title: verb + object (e.g., "Delete document", "Close incident")
 *   description: what happens + recovery path ("This will remove the file.
 *                 You can restore it within 30 days from Audit Log.")
 *   confirmLabel: the verb ("Delete", "Close incident")
 *
 * Built on the same pattern as Dialog — portal, focus trap, escape-to-close.
 */
export function AlertDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  destructive = false,
  loading = false,
  confirmDisabled = false,
  children,
}: AlertDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const descId = `${dialogId}-description`;

  useModalLayer({
    open,
    panelRef: dialogRef,
    onClose,
    closeOnEscape: !loading,
    focusableSelector:
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  });

  useEffect(() => {
    if (!open) return;
    // Keep the least destructive action focused after shared-stack admission.
    const focusFrame = requestAnimationFrame(() => {
      if (dialogRef.current?.contains(document.activeElement)) cancelButtonRef.current?.focus();
    });
    return () => cancelAnimationFrame(focusFrame);
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    await onConfirm();
  };

  const content = (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center p-4"
      role="alertdialog"
      data-modal-layer=""
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
    >
      <div
        className="fixed inset-0 bg-surface-inverse/40 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-overlay bg-surface-sheet shadow-dialog',
          'motion-reduce:animate-none animate-in fade-in-0 zoom-in-95 duration-150',
        )}
      >
        <div className="flex min-h-0 flex-1 items-start gap-4 overflow-y-auto overscroll-y-contain px-6 py-6">
          {destructive && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-danger-bg">
              <AlertTriangle className="h-5 w-5 text-status-danger-fg" aria-hidden="true" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="font-body text-card-title text-text-heading">
              {title}
            </h2>
            {description && (
              <div id={descId} className="mt-2 text-body-sm text-text-muted">
                {description}
              </div>
            )}
            {children && <div className="mt-4">{children}</div>}
          </div>
        </div>
        <div className="flex shrink-0 flex-col flex-wrap items-stretch gap-2 border-t border-border bg-surface-soft px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            data-ui="button"
            className={buttonVariants({ variant: 'secondary' })}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={handleConfirm}
            disabled={loading || confirmDisabled}
            data-ui="button"
            className={buttonVariants({ variant: destructive ? 'danger' : 'primary' })}
          >
            {loading && (
              <Loader2
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
